/*
 * Audiocolour Frequency to Pitch Calculator — interface.
 * Ordinary deferred script. Requires audiocolour-frequency-pitch-core.js
 * (globalThis.AudiocolourFrequencyPitchCore) and, for sound,
 * audiocolour-frequency-pitch-audio.js (globalThis.AudiocolourFrequencyPitchAudio,
 * contract v1.0) to be loaded first. Every calculated value comes from the
 * core and every sound from the audio engine; this file only reads inputs,
 * handles events and formats results. One instance per [data-ac-freq-pitch]
 * root, each with its own A4 reference, notation, volume and audio client.
 */
(function () {
  "use strict";

  // Guard against the script being included twice (e.g. two sections on a page).
  if (window.AudiocolourFrequencyPitch) return;

  var COPY_FEEDBACK_MS = 1500;
  var MINUS = "−";
  var LOW_HINT_HZ = 40;
  var HIGH_HINT_HZ = 15000;
  var PANELS = ["convert", "shift", "harmonics"];
  var PANEL_NAMES = { convert: "Convert", shift: "Pitch shift", harmonics: "Harmonics" };
  // Sounds whose frequency depends on the A4 reference (spec §6.8, §7).
  var NOTE_SOURCES = { "convert-input-note": true, "convert-nearest-note": true };
  var SEMITONE_MESSAGE = "Enter a whole number of semitones from −48 to +48.";
  var CENTS_MESSAGE = "Enter cents from −100 to +100, with up to one decimal place.";
  var A4_MESSAGE = "Set an A4 reference between 400 and 480 Hz.";
  var FIELD_MESSAGES = {
    frequency: {
      empty: "Enter a frequency between 16 and 20,000 Hz.",
      range: "Frequency must be between 16 and 20,000 Hz.",
      number: "Enter a number, e.g. 440 or 261.63. Use a dot for decimals."
    },
    reference: {
      empty: A4_MESSAGE,
      range: A4_MESSAGE,
      number: "Enter a number, e.g. 440 or 415.3. Use a dot for decimals.",
      step: "Use up to two decimal places, e.g. 440.25."
    },
    semitones: { empty: SEMITONE_MESSAGE, range: SEMITONE_MESSAGE, number: SEMITONE_MESSAGE, step: SEMITONE_MESSAGE },
    cents: { empty: CENTS_MESSAGE, range: CENTS_MESSAGE, number: CENTS_MESSAGE, step: CENTS_MESSAGE }
  };
  // Approved playback messages (spec §3.3).
  var PLAY_MESSAGES = {
    below_min_frequency: "Too low to play. The tone generator starts at 20 Hz.",
    above_max_frequency: "Too high to play. The tone generator stops at 20,000 Hz.",
    unsupported: "Sound isn't available in this browser. All calculations still work.",
    context_failed: "Sound couldn't start. Check your device's sound output and try again.",
    playback_blocked: "Your browser blocked sound. Select Play again to allow it."
  };
  var STOP_MESSAGES = {
    auto_stop: "Tone stopped after 60 seconds.",
    replaced: "Sound stopped because another calculator on this page started playing.",
    context_interrupted: "Sound stopped because another app took over audio.",
    context_error: "Sound stopped because of an audio error. Select Play to try again."
  };
  var A4_STOP_MESSAGE = "Sound stopped because the A4 reference isn't valid.";
  var LOW_HINT = "Many phone and laptop speakers can't reproduce this.";
  var HIGH_HINT = "This may be inaudible. Don't raise the volume to find it.";
  var RANGE_WARNINGS = {
    below_supported_range: "Below the calculator's 16 Hz – 20 kHz range. The value is mathematically correct.",
    above_supported_range: "Above the calculator's 16 Hz – 20 kHz range. The value is mathematically correct.",
    outside_midi_range: "Outside the MIDI note range (0–127). The note name is mathematically correct, but no standard MIDI note exists for it."
  };

  var instances = new WeakMap();
  var numberFormats = {};

  // ---- Formatting (display only; no calculation) ---------------------------

  function toArray(list) { return Array.prototype.slice.call(list); }

  function grouped(value, minDigits, maxDigits) {
    var key = minDigits + "-" + maxDigits;
    if (!numberFormats[key]) {
      numberFormats[key] = new Intl.NumberFormat("en-US", { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits });
    }
    return displayMinus(numberFormats[key].format(value));
  }

  function displayMinus(text) { return text.replace(/-/g, MINUS); }

  // Plain number for copying: no grouping, ASCII minus, never "-0".
  function plain(value, digits) {
    var text = value.toFixed(digits);
    return Number(text) === 0 ? (0).toFixed(digits) : text;
  }

  // Display with an explicit sign ("+3.9", "−3.9", "0.0").
  function signed(value, digits) {
    var magnitude = Math.abs(value).toFixed(digits);
    if (Number(magnitude) === 0) return magnitude;
    return (value < 0 ? MINUS : "+") + magnitude;
  }

  // Copy form of a signed value: ASCII minus, explicit plus ("+3.9", "-3.9").
  function signedCopy(value, digits) {
    var magnitude = Math.abs(value).toFixed(digits);
    if (Number(magnitude) === 0) return magnitude;
    return (value < 0 ? "-" : "+") + magnitude;
  }

  function hz(value) { return grouped(value, 3, 3) + " Hz"; }
  function hzShort(value) { return grouped(value, 0, 3) + " Hz"; }
  function integerText(value) { return displayMinus(String(value)); }
  function referenceText(referenceA4) { return String(Number(referenceA4.toFixed(2))) + " Hz"; }
  function noteWithCents(pitch) { return pitch.name + " " + signed(pitch.cents, 1); }
  function plural(count, one, many) { return count + " " + (Math.abs(count) === 1 ? one : many); }

  function warningText(codes) {
    var seen = {};
    return codes.filter(function (code) {
      if (seen[code] || !RANGE_WARNINGS[code]) return false;
      seen[code] = true;
      return true;
    }).map(function (code) { return RANGE_WARNINGS[code]; }).join(" ");
  }

  function playMessage(error, blend) {
    if (!error) return "";
    if (error.code === "above_device_limit") {
      var limit = grouped(error.limit, 0, 0);
      return blend
        ? "Some harmonics are too high for your audio device, which plays up to " + limit + " Hz. Select Play again to hear the rest."
        : "Too high for your audio device, which plays up to " + limit + " Hz.";
    }
    return PLAY_MESSAGES[error.code] || "";
  }

  function createInstance(root, core, audioApi) {
    var tablist = root.querySelector("[data-ac-freq-pitch-tablist]");
    var status = root.querySelector("[data-ac-freq-pitch-status]");
    var settings = root.querySelector("[data-ac-freq-pitch-settings]");
    var audioNotice = root.querySelector("[data-ac-freq-pitch-audio-unsupported]");
    var tabs = toArray(root.querySelectorAll("[data-ac-freq-pitch-tab]"));
    var panels = {};
    PANELS.forEach(function (name) {
      panels[name] = root.querySelector('[data-ac-freq-pitch-panel="' + name + '"]');
    });
    if (!tablist || !status || !settings || !audioNotice || tabs.length !== PANELS.length ||
        PANELS.some(function (name) { return !panels[name]; })) return null;

    var tableHead = panels.convert.querySelector("[data-table-head]");
    var tableBody = panels.convert.querySelector("[data-table-body]");
    var copyTable = panels.convert.querySelector("[data-copy-table]");
    var copyFeedback = panels.convert.querySelector("[data-copy-feedback]");
    var harmonicsBody = panels.harmonics.querySelector("[data-harmonics-body]");
    var stopSoundButton = settings.querySelector("[data-stop-sound]");
    var soundMessage = settings.querySelector('[data-output="soundMessage"]');
    var volumeOutput = settings.querySelector('[data-output="volume"]');
    if (!tableHead || !tableBody || !copyTable || !copyFeedback || !harmonicsBody ||
        !stopSoundButton || !soundMessage || !volumeOutput) return null;

    var controller = new AbortController();
    var timers = { announce: null, copy: null, feedback: null };
    var values = { settings: {}, convert: {}, shift: {}, harmonics: {} }; // validated field values (or null)
    var state = {
      referenceA4: null,
      notation: "sharp",
      volume: 40,
      currentMidi: null,     // note highlighted in the reference table
      tableExport: null,     // latest valid reference table, for Copy table
      harmonicsKey: null,    // fundamental|count, to detect D2 changes
      copySequence: 0,
      copyButton: null
    };
    // Latest results that sounds are derived from.
    var sources = {
      convert: { mode: "frequency", input: null, nearest: null, note: null },
      shift: { source: null, result: null },
      harmonics: { rows: [] }
    };
    var table = { headers: [], cells: [] };
    var client = null;
    var sound = null; // this instance's sound: { playbackId, sourceId, kind, status, frequency }

    function on(target, type, handler) {
      target.addEventListener(type, handler, { signal: controller.signal });
    }

    function clearTimer(name) {
      if (timers[name] !== null) {
        window.clearTimeout(timers[name]);
        timers[name] = null;
      }
    }

    function announce(message) {
      clearTimer("announce");
      status.textContent = "";
      timers.announce = window.setTimeout(function () {
        status.textContent = message;
        timers.announce = null;
      }, 20);
    }

    function field(scope, name) { return scope.querySelector('[data-field="' + name + '"]'); }
    function output(scope, name) { return scope.querySelector('[data-output="' + name + '"]'); }
    function scopeElement(name) { return name === "settings" ? settings : panels[name]; }

    function setText(scope, name, text) {
      var el = output(scope, name);
      if (el) el.textContent = text;
    }

    function setDisabled(button, disabled) {
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
    }

    function isDisabled(button) {
      return button.getAttribute("aria-disabled") === "true";
    }

    // Stores the exact text a copy button will place on the clipboard.
    function setCopyValue(button, value) {
      var label = button.getAttribute("data-copy-label") || "value";
      if (value === null) {
        button.removeAttribute("data-value");
        button.setAttribute("aria-label", "Copy " + label + ", no value");
        setDisabled(button, true);
      } else {
        button.setAttribute("data-value", value);
        button.setAttribute("aria-label", "Copy " + label + ", " + value);
        setDisabled(button, false);
      }
    }

    function show(scope, name, text, copyValue) {
      setText(scope, name, text);
      var button = scope.querySelector('[data-copy="' + name + '"]');
      if (button) setCopyValue(button, copyValue === undefined ? null : copyValue);
    }

    function clearOutputs(scope, names) {
      names.forEach(function (name) { show(scope, name, "—", null); });
    }

    function setResult(panel, result) {
      panel.setAttribute("data-result", result);
    }

    function setWarning(panel, codes) {
      var el = output(panel, "rangeWarning");
      var text = warningText(codes);
      el.textContent = text;
      el.hidden = !text;
    }

    // ---- Select options (from the core's supported lists) -------------------

    function populateSelects() {
      toArray(root.querySelectorAll("select[data-options]")).forEach(function (select) {
        var type = select.getAttribute("data-options");
        var options = [];
        if (type === "pitch-classes") {
          for (var pitchClass = 0; pitchClass < 12; pitchClass += 1) {
            options.push({ value: String(pitchClass), text: core.noteNames[state.notation][pitchClass] });
          }
        } else if (type === "octaves") {
          toArray(core.tableOctaves).forEach(function (octave) { options.push({ value: String(octave), text: String(octave) }); });
        } else if (type === "harmonic-counts") {
          toArray(core.harmonicCounts).forEach(function (count) { options.push({ value: String(count), text: String(count) }); });
        }
        select.textContent = "";
        options.forEach(function (option) {
          var el = document.createElement("option");
          el.value = option.value;
          el.textContent = option.text;
          select.appendChild(el);
        });
        resetSelect(select);
      });
    }

    function resetSelect(select) {
      var preferred = select.getAttribute("data-default");
      var options = toArray(select.options);
      if (preferred === null) {
        var marked = options.filter(function (option) { return option.defaultSelected; })[0];
        preferred = marked ? marked.value : null;
      }
      var match = options.some(function (option) { return option.value === preferred; });
      select.value = match ? preferred : (options[0] ? options[0].value : "");
    }

    // Notation changes the note labels only; the selected pitch class stays.
    function relabelNoteOptions() {
      toArray(root.querySelectorAll('select[data-options="pitch-classes"]')).forEach(function (select) {
        toArray(select.options).forEach(function (option) {
          option.textContent = core.noteNames[state.notation][Number(option.value)];
        });
      });
    }

    // ---- Field validation ----------------------------------------------------

    function setFieldError(scope, input, message, invalid) {
      var errorEl = scope.querySelector('[data-error-for="' + input.getAttribute("data-field") + '"]');
      if (errorEl) {
        errorEl.textContent = message || "";
        errorEl.hidden = !message;
      }
      if (invalid) input.setAttribute("aria-invalid", "true");
      else input.removeAttribute("aria-invalid");
    }

    // Returns "ok", "low" (below the minimum, possibly mid-typing), "range" or "step".
    function checkKind(kind, value) {
      if (kind === "frequency") {
        if (core.validateEnteredFrequency(value).ok) return "ok";
        return value > 0 && value < core.frequencyRange.min ? "low" : "range";
      }
      if (kind === "reference") {
        var reference = core.validateReference(value);
        if (reference.ok) return "ok";
        if (reference.error.code === "reference_precision") return "step";
        return value > 0 && value < core.referenceRange.min ? "low" : "range";
      }
      if (kind === "semitones") {
        if (!Number.isInteger(value)) return "step";
        return value >= core.semitoneRange.min && value <= core.semitoneRange.max ? "ok" : "range";
      }
      if (value < core.centsRange.min || value > core.centsRange.max) return "range";
      return Math.abs(value * 10 - Math.round(value * 10)) < 1e-9 ? "ok" : "step";
    }

    // Returns { value, message, invalid }. While typing ("input"), an empty,
    // incomplete or too-low value is not flagged yet; on "change" it is.
    function readField(input, eventType) {
      var kind = input.getAttribute("data-kind");
      var messages = FIELD_MESSAGES[kind];
      var committed = eventType === "change";
      var pending = { value: null, message: "", invalid: false };
      if (input.validity && input.validity.badInput) {
        return committed ? { value: null, message: messages.number, invalid: true } : pending;
      }
      if (String(input.value).trim() === "") {
        return committed ? { value: null, message: messages.empty, invalid: true } : pending;
      }
      var value = input.valueAsNumber;
      if (!Number.isFinite(value)) return { value: null, message: messages.number, invalid: true };
      var check = checkKind(kind, value);
      if (check === "ok") return { value: value, message: "", invalid: false };
      if (check === "low" && !committed) return pending;
      return { value: null, message: messages[check === "step" ? "step" : "range"], invalid: true };
    }

    function updateField(scopeName, input, eventType) {
      var result = readField(input, eventType);
      values[scopeName][input.getAttribute("data-field")] = result.value;
      setFieldError(scopeElement(scopeName), input, result.message, result.invalid);
    }

    function readAllFields(scopeName, eventType) {
      toArray(scopeElement(scopeName).querySelectorAll("input[data-kind]")).forEach(function (input) {
        updateField(scopeName, input, eventType);
      });
    }

    // ---- Settings: A4 reference, notation, volume ------------------------------

    function readReference() {
      var custom = field(settings, "referencePreset").value === "custom";
      toArray(settings.querySelectorAll("[data-reference-custom]")).forEach(function (el) { el.hidden = !custom; });
      if (!custom) {
        setFieldError(settings, field(settings, "referenceCustom"), "", false);
        return Number(field(settings, "referencePreset").value);
      }
      return values.settings.referenceCustom;
    }

    function readNotation() {
      var checked = settings.querySelector('[data-field="notation"]:checked');
      return checked && checked.value === "flat" ? "flat" : "sharp";
    }

    function readVolume() {
      var volume = Number(field(settings, "volume").value);
      return Number.isFinite(volume) ? Math.max(0, Math.min(100, volume)) : 40;
    }

    function applyVolume() {
      state.volume = readVolume();
      volumeOutput.textContent = state.volume + "%";
      if (client) client.setVolume(state.volume);
    }

    // ---- Workflow 1: Convert -------------------------------------------------

    var CONVERT_OUTPUTS = ["nearestNote", "cents", "nearestFrequency", "midiNote", "decimalPitch", "noteFrequency", "noteMidi"];

    function convertMode() {
      var checked = panels.convert.querySelector('[data-field="mode"]:checked');
      return checked && checked.value === "note" ? "note" : "frequency";
    }

    function clearConvert(summary) {
      var panel = panels.convert;
      clearOutputs(panel, CONVERT_OUTPUTS);
      ["enharmonic", "centsDirection"].forEach(function (name) { setText(panel, name, ""); });
      setText(panel, "noteEnharmonic", "—");
      setText(panel, "summary", summary);
      setWarning(panel, []);
      setResult(panel, "invalid");
    }

    function renderConvert() {
      var panel = panels.convert;
      var mode = convertMode();
      var reference = state.referenceA4;
      toArray(panel.querySelectorAll("[data-mode-group]")).forEach(function (el) {
        el.hidden = el.getAttribute("data-mode-group") !== mode;
      });
      sources.convert = { mode: mode, input: null, nearest: null, note: null };
      state.currentMidi = null;

      if (mode === "frequency") {
        var frequency = values.convert.frequency;
        sources.convert.input = frequency == null ? null : frequency;
        if (frequency == null) {
          clearConvert("Enter a frequency to find its note.");
        } else if (reference == null) {
          clearConvert(A4_MESSAGE);
        } else {
          renderFrequencyToNote(frequency, reference);
        }
      } else if (reference == null) {
        clearConvert(A4_MESSAGE);
      } else {
        renderNoteToFrequency(reference);
      }
      renderTable();
    }

    function renderFrequencyToNote(frequency, reference) {
      var panel = panels.convert;
      var pitch = core.frequencyToPitch({ frequency: frequency, referenceA4: reference, notation: state.notation });
      if (!pitch.ok) {
        clearConvert(pitch.error.message);
        return;
      }
      show(panel, "nearestNote", pitch.name, pitch.asciiName);
      setText(panel, "enharmonic", pitch.enharmonicName ? "also " + pitch.enharmonicName : "");
      show(panel, "cents", signed(pitch.cents, 1), signedCopy(pitch.cents, 1));
      setText(panel, "centsDirection", pitch.direction === "exact" ? "cents, exactly on the note" : "cents " + pitch.direction + " " + pitch.name);
      if (pitch.nearestFrequencyStatus === "ok") {
        show(panel, "nearestFrequency", hz(pitch.nearestFrequency), plain(pitch.nearestFrequency, 3));
        sources.convert.nearest = pitch.nearestFrequency;
      } else {
        show(panel, "nearestFrequency", "—", null);
      }
      show(panel, "midiNote", integerText(pitch.midiNote), String(pitch.midiNote));
      show(panel, "decimalPitch", displayMinus(plain(pitch.decimalPitch, 3)), plain(pitch.decimalPitch, 3));
      state.currentMidi = pitch.midiNote;

      var summary = hzShort(frequency) + " is ";
      summary += pitch.direction === "exact"
        ? "exactly " + pitch.name
        : Math.abs(pitch.cents).toFixed(1) + " cents " + pitch.direction + " " + pitch.name;
      setText(panel, "summary", summary + " (" + referenceText(reference) + " reference).");
      setWarning(panel, toArray(pitch.rangeWarnings));
      setResult(panel, "ready");
    }

    function renderNoteToFrequency(reference) {
      var panel = panels.convert;
      var note = core.noteToFrequency({
        pitchClass: Number(field(panel, "pitchClass").value),
        octave: Number(field(panel, "octave").value),
        referenceA4: reference,
        notation: state.notation
      });
      if (!note.ok) {
        clearConvert(note.error.message);
        return;
      }
      show(panel, "noteFrequency", hz(note.frequency), plain(note.frequency, 3));
      show(panel, "noteMidi", integerText(note.midiNote), String(note.midiNote));
      setText(panel, "noteEnharmonic", note.enharmonicName || "No other name");
      sources.convert.note = note.frequency;
      state.currentMidi = note.midiNote;
      setText(panel, "summary", note.name + " is " + hz(note.frequency) + " (" + referenceText(reference) + " reference).");
      setWarning(panel, []);
      setResult(panel, "ready");
    }

    // ---- Reference table (cells from the core's table) -----------------------

    function buildTable() {
      while (tableHead.children.length > 1) tableHead.removeChild(tableHead.lastElementChild);
      tableBody.textContent = "";
      table = { headers: [], cells: [] };
      toArray(core.tableOctaves).forEach(function (octave) {
        var th = document.createElement("th");
        th.scope = "col";
        th.textContent = String(octave);
        tableHead.appendChild(th);
      });
      for (var pitchClass = 0; pitchClass < 12; pitchClass += 1) {
        var row = document.createElement("tr");
        var heading = document.createElement("th");
        heading.scope = "row";
        row.appendChild(heading);
        table.headers.push(heading);
        toArray(core.tableOctaves).forEach(function (octave) {
          var cell = document.createElement("td");
          var button = document.createElement("button");
          button.type = "button";
          button.className = "ac-freq-pitch-cell__copy";
          button.setAttribute("data-copy", "cell");
          button.setAttribute("data-midi", String(12 * (octave + 1) + pitchClass));
          setCopyValue(button, null);
          cell.appendChild(button);
          row.appendChild(cell);
          table.cells.push(button);
        });
        tableBody.appendChild(row);
      }
    }

    function renderTable() {
      var result = state.referenceA4 == null ? null : core.referenceTable({ referenceA4: state.referenceA4, notation: state.notation });
      var byMidi = {};
      if (result && result.ok) {
        toArray(result.rows).forEach(function (row, index) {
          table.headers[index].textContent = row.name;
          toArray(row.cells).forEach(function (cell) { byMidi[cell.midiNote] = cell; });
        });
      } else {
        table.headers.forEach(function (heading, pitchClass) { heading.textContent = core.noteNames[state.notation][pitchClass]; });
      }
      table.cells.forEach(function (button) {
        var midi = Number(button.getAttribute("data-midi"));
        var cell = byMidi[midi];
        if (!cell) {
          button.textContent = "—";
          button.setAttribute("data-copy-label", "note frequency");
          setCopyValue(button, null);
          button.removeAttribute("aria-current");
          return;
        }
        button.textContent = grouped(cell.frequency, 2, 2);
        button.setAttribute("data-copy-label", cell.name + " frequency in hertz");
        setCopyValue(button, plain(cell.frequency, 2));
        if (midi === state.currentMidi) button.setAttribute("aria-current", "true");
        else button.removeAttribute("aria-current");
      });
      state.tableExport = result && result.ok ? result : null;
      setDisabled(copyTable, !state.tableExport);
    }

    function tableExportText() {
      var data = state.tableExport;
      var lines = [
        "Audiocolour Frequency to Pitch reference table",
        "A4 reference\t" + referenceText(data.referenceA4),
        "Notation\t" + (data.notation === "flat" ? "Flats" : "Sharps"),
        "",
        ["Note"].concat(toArray(data.octaves).map(String)).join("\t")
      ];
      toArray(data.rows).forEach(function (row) {
        lines.push([row.asciiName].concat(toArray(row.cells).map(function (cell) { return plain(cell.frequency, 2); })).join("\t"));
      });
      lines.push("", "Frequencies in hertz, rounded to two decimal places. Octave 4 contains middle C (C4, MIDI 60).");
      return lines.join("\n");
    }

    // ---- Workflow 2: Pitch shift -----------------------------------------------

    var SHIFT_OUTPUTS = ["shiftedFrequency", "shiftedNote", "ratio", "speedChangePercent", "lengthPercent", "totalSemitones"];

    function clearShift(summary) {
      var panel = panels.shift;
      clearOutputs(panel, SHIFT_OUTPUTS);
      setText(panel, "shiftedCents", "");
      setText(panel, "summary", summary);
      setWarning(panel, []);
      setResult(panel, "invalid");
    }

    function renderChips() {
      toArray(panels.shift.querySelectorAll("[data-quick-semitones]")).forEach(function (chip) {
        var pressed = Number(chip.getAttribute("data-quick-semitones")) === values.shift.semitones;
        chip.setAttribute("aria-pressed", pressed ? "true" : "false");
      });
    }

    function renderShift() {
      var panel = panels.shift;
      var v = values.shift;
      renderChips();
      sources.shift = { source: v.frequency == null ? null : v.frequency, result: null };
      if (v.frequency == null || v.semitones == null || v.cents == null) {
        clearShift("Enter a source frequency and a shift to calculate.");
        return;
      }
      var shift = core.pitchShift({ frequency: v.frequency, semitones: v.semitones, cents: v.cents });
      if (!shift.ok) {
        clearShift(shift.error.message);
        return;
      }
      sources.shift.result = shift.shiftedFrequency;
      var warnings = toArray(shift.rangeWarnings);
      var noteText = "";
      show(panel, "shiftedFrequency", hz(shift.shiftedFrequency), plain(shift.shiftedFrequency, 3));
      if (state.referenceA4 == null) {
        show(panel, "shiftedNote", "—", null);
        setText(panel, "shiftedCents", A4_MESSAGE);
      } else {
        var pitch = core.frequencyToPitch({ frequency: shift.shiftedFrequency, referenceA4: state.referenceA4, notation: state.notation });
        if (pitch.ok) {
          show(panel, "shiftedNote", pitch.name, pitch.asciiName);
          setText(panel, "shiftedCents", signed(pitch.cents, 1) + " cents");
          warnings = toArray(pitch.rangeWarnings);
          noteText = " (" + noteWithCents(pitch) + " cents)";
        } else {
          show(panel, "shiftedNote", "—", null);
          setText(panel, "shiftedCents", "");
        }
      }
      show(panel, "ratio", plain(shift.ratio, 6), plain(shift.ratio, 6));
      show(panel, "speedChangePercent", signed(shift.speedChangePercent, 3) + "%", plain(shift.speedChangePercent, 3));
      show(panel, "lengthPercent", grouped(shift.lengthPercent, 3, 3) + "%", plain(shift.lengthPercent, 3));
      show(panel, "totalSemitones", signed(shift.totalSemitones, 3), plain(shift.totalSemitones, 3));

      var amounts = [];
      if (v.semitones !== 0) amounts.push(signed(v.semitones, 0) + " " + (Math.abs(v.semitones) === 1 ? "semitone" : "semitones"));
      if (v.cents !== 0) amounts.push(signed(v.cents, 1) + " cents");
      setText(panel, "summary", amounts.length
        ? "Shifting " + hzShort(v.frequency) + " by " + amounts.join(" and ") + " gives " + hz(shift.shiftedFrequency) + noteText + "."
        : "No shift: " + hzShort(v.frequency) + " stays at " + hz(shift.shiftedFrequency) + noteText + ".");
      setWarning(panel, warnings);
      setResult(panel, "ready");
    }

    // ---- Workflow 3: Harmonics -------------------------------------------------

    function harmonicsCount() {
      return Number(field(panels.harmonics, "count").value);
    }

    function harmonicCell(row, className, text) {
      var cell = document.createElement("td");
      if (className) cell.className = className;
      if (text !== undefined) cell.textContent = text;
      row.appendChild(cell);
      return cell;
    }

    function harmonicRow(entry) {
      var row = document.createElement("tr");
      row.setAttribute("data-harmonic", String(entry.harmonic));
      row.setAttribute("data-in-range", entry.inSupportedRange ? "true" : "false");
      var number = document.createElement("th");
      number.scope = "row";
      number.textContent = String(entry.harmonic);
      row.appendChild(number);

      var nameCell = harmonicCell(row);
      var name = document.createElement("span");
      name.className = "ac-freq-pitch-harmonic__name";
      name.textContent = entry.harmonic === 1 ? "Fundamental" : "Overtone " + entry.overtone;
      var detail = document.createElement("span");
      detail.className = "ac-freq-pitch-harmonic__detail";
      detail.setAttribute("data-harmonic-detail", "");
      detail.hidden = true;
      nameCell.appendChild(name);
      nameCell.appendChild(detail);

      harmonicCell(row, "", hz(entry.frequency));
      harmonicCell(row).setAttribute("data-harmonic-note", "");
      harmonicCell(row, "", plain(entry.intervalSemitones, 2) + " st");

      var play = document.createElement("button");
      play.type = "button";
      play.className = "ac-freq-pitch-play";
      play.setAttribute("data-play", "harmonic");
      play.setAttribute("data-harmonic", String(entry.harmonic));
      play.setAttribute("aria-pressed", "false");
      play.setAttribute("aria-label", "Play harmonic " + entry.harmonic + ", " + hz(entry.frequency));
      var icon = document.createElement("span");
      icon.className = "ac-freq-pitch-play__icon";
      icon.setAttribute("aria-hidden", "true");
      var playText = document.createElement("span");
      playText.setAttribute("data-play-text", "");
      playText.setAttribute("data-idle-text", "Play");
      playText.textContent = "Play";
      play.appendChild(icon);
      play.appendChild(playText);
      harmonicCell(row).appendChild(play);

      var copy = document.createElement("button");
      copy.type = "button";
      copy.className = "ac-freq-pitch-copy";
      copy.setAttribute("data-copy", "harmonic");
      copy.setAttribute("data-copy-label", "harmonic " + entry.harmonic + " frequency in hertz");
      var copyText = document.createElement("span");
      copyText.setAttribute("data-copy-text", "");
      copyText.setAttribute("data-idle-text", "Copy");
      copyText.textContent = "Copy";
      copy.appendChild(copyText);
      setCopyValue(copy, plain(entry.frequency, 3));
      harmonicCell(row).appendChild(copy);
      return row;
    }

    // Rebuilds the rows, keeping keyboard focus on the same row's control.
    function renderHarmonics() {
      var panel = panels.harmonics;
      var fundamental = values.harmonics.fundamental;
      var focused = document.activeElement && harmonicsBody.contains(document.activeElement) ? document.activeElement : null;
      var focusRow = focused ? focused.closest("tr[data-harmonic]") : null;
      var focusSelector = focused && focused.hasAttribute("data-copy") ? '[data-copy="harmonic"]' : '[data-play="harmonic"]';
      var focusHarmonic = focusRow ? focusRow.getAttribute("data-harmonic") : null;
      harmonicsBody.textContent = "";
      sources.harmonics = { rows: [] };
      state.harmonicsKey = fundamental == null ? null : fundamental + "|" + harmonicsCount();

      if (fundamental == null) {
        setText(panel, "summary", "Enter a fundamental frequency to generate its harmonics.");
        setResult(panel, "invalid");
        return;
      }
      var series = core.harmonicSeries({ fundamental: fundamental, count: harmonicsCount() });
      if (!series.ok) {
        setText(panel, "summary", series.error.message);
        setResult(panel, "invalid");
        return;
      }
      sources.harmonics.rows = toArray(series.rows);
      sources.harmonics.rows.forEach(function (entry) { harmonicsBody.appendChild(harmonicRow(entry)); });
      renderHarmonicNotes();
      renderHarmonicsSummary();
      setResult(panel, "ready");

      if (focusHarmonic) {
        var row = harmonicsBody.querySelector('tr[data-harmonic="' + focusHarmonic + '"]');
        var target = row ? row.querySelector(focusSelector) : null;
        if (target) target.focus();
      }
    }

    // Note names depend on A4 and notation; frequencies do not.
    function renderHarmonicNotes() {
      toArray(harmonicsBody.querySelectorAll("tr[data-harmonic]")).forEach(function (row) {
        var entry = sources.harmonics.rows[Number(row.getAttribute("data-harmonic")) - 1];
        var cell = row.querySelector("[data-harmonic-note]");
        if (!entry || !cell) return;
        if (state.referenceA4 == null) {
          cell.textContent = "—";
          return;
        }
        var pitch = core.frequencyToPitch({ frequency: entry.frequency, referenceA4: state.referenceA4, notation: state.notation });
        cell.textContent = pitch.ok ? noteWithCents(pitch) : "—";
      });
    }

    var RENDERERS = { convert: renderConvert, shift: renderShift, harmonics: renderHarmonics };

    function renderAll() {
      renderConvert();
      renderShift();
      renderHarmonicNotes();
      renderHarmonicsSummary();
      renderSoundControls();
    }

    // The harmonics summary names the fundamental's note, which follows A4.
    function renderHarmonicsSummary() {
      if (!sources.harmonics.rows.length) return;
      var panel = panels.harmonics;
      var fundamental = values.harmonics.fundamental;
      var last = sources.harmonics.rows[sources.harmonics.rows.length - 1];
      var fundamentalName = "";
      if (state.referenceA4 != null) {
        var pitch = core.frequencyToPitch({ frequency: fundamental, referenceA4: state.referenceA4, notation: state.notation });
        if (pitch.ok) fundamentalName = " (" + pitch.name + ")";
      }
      setText(panel, "summary", plural(sources.harmonics.rows.length, "harmonic", "harmonics") + " of " + hzShort(fundamental) +
        fundamentalName + ", up to " + hzShort(last.frequency) + ".");
    }

    // ---- Sound sources and playability -------------------------------------------

    function sourceIdFor(button) {
      var play = button.getAttribute("data-play");
      if (play === "convert-input") return sources.convert.mode === "note" ? "convert-input-note" : "convert-input-frequency";
      if (play === "convert-nearest") return "convert-nearest-note";
      if (play === "harmonic") return "harmonic-" + button.getAttribute("data-harmonic");
      return play;
    }

    function panelOfSource(sourceId) {
      if (sourceId.indexOf("convert-") === 0) return "convert";
      if (sourceId.indexOf("shift-") === 0) return "shift";
      return "harmonics";
    }

    // The frequency a tone source currently stands for, or null.
    function sourceFrequency(sourceId) {
      var convert = sources.convert;
      if (sourceId === "convert-input-frequency") return convert.mode === "frequency" ? convert.input : null;
      if (sourceId === "convert-input-note") return convert.mode === "note" ? convert.note : null;
      if (sourceId === "convert-nearest-note") return convert.mode === "frequency" ? convert.nearest : null;
      if (sourceId === "shift-source") return sources.shift.source;
      if (sourceId === "shift-result") return sources.shift.result;
      var match = /^harmonic-(\d+)$/.exec(sourceId);
      if (match) {
        var entry = sources.harmonics.rows[Number(match[1]) - 1];
        return entry ? entry.frequency : null;
      }
      return null;
    }

    function checkPlayable(frequency) {
      if (!client) return { ok: false, error: { code: "unsupported" } };
      if (frequency == null) return { ok: false, error: { code: "invalid_frequency" } };
      return audioApi.checkFrequency(frequency);
    }

    // Partials for the playable harmonics, gains matched by harmonic number (§5.1).
    function blendPartials() {
      var playable = sources.harmonics.rows.filter(function (entry) { return checkPlayable(entry.frequency).ok; });
      if (!playable.length) return null;
      var gains = core.harmonicGains(playable.map(function (entry) { return entry.harmonic; }));
      if (!gains.ok) return null;
      var lookup = {};
      toArray(gains.gains).forEach(function (gain) { lookup[gain.harmonic] = gain; });
      return playable.map(function (entry) { return { frequency: entry.frequency, gain: lookup[entry.harmonic].gain }; });
    }

    function toneHint(frequency) {
      if (!client || frequency == null) return "";
      var check = checkPlayable(frequency);
      if (!check.ok) return playMessage(check.error, false);
      if (frequency < LOW_HINT_HZ) return LOW_HINT;
      if (frequency > HIGH_HINT_HZ) return HIGH_HINT;
      return "";
    }

    function setToneHint(panelName, text) {
      var el = output(panels[panelName], "toneHint");
      el.textContent = text;
      el.hidden = !text;
    }

    function renderSoundControls() {
      toArray(root.querySelectorAll("[data-play]")).forEach(function (button) {
        var sourceId = sourceIdFor(button);
        var pressed = !!sound && sound.sourceId === sourceId;
        var available = pressed || (sourceId === "harmonics-blend"
          ? !!blendPartials()
          : checkPlayable(sourceFrequency(sourceId)).ok);
        button.setAttribute("aria-pressed", pressed ? "true" : "false");
        if (pressed && sound.status === "starting") button.setAttribute("aria-busy", "true");
        else button.removeAttribute("aria-busy");
        setDisabled(button, !available);
        var label = button.querySelector("[data-play-text]");
        if (label) label.textContent = pressed ? "Stop" : label.getAttribute("data-idle-text");
      });

      toArray(harmonicsBody.querySelectorAll("tr[data-harmonic]")).forEach(function (row) {
        var detail = row.querySelector("[data-harmonic-detail]");
        var frequency = sourceFrequency("harmonic-" + row.getAttribute("data-harmonic"));
        var check = checkPlayable(frequency);
        var reason = client && !check.ok ? playMessage(check.error, false).split(". ")[0] : "";
        detail.textContent = reason ? reason.replace(/\.?$/, ".") : "";
        detail.hidden = !reason;
      });

      setDisabled(stopSoundButton, !sound);
      setToneHint("convert", toneHint(sourceFrequency(sources.convert.mode === "note" ? "convert-input-note" : "convert-input-frequency")));
      setToneHint("shift", toneHint(sources.shift.result));
      setToneHint("harmonics", toneHint(values.harmonics.fundamental == null ? null : values.harmonics.fundamental));
    }

    function showSoundMessage(message) {
      soundMessage.textContent = message;
      soundMessage.hidden = !message;
      if (message) announce(message);
    }

    // ---- Playback (audio engine contract v1.0, spec §6–§7) ----------------------

    function togglePlay(button) {
      if (!client || !root.contains(button)) return;
      var sourceId = sourceIdFor(button);
      if (sound && sound.sourceId === sourceId) {
        stopSound("");
        announce("Sound stopped.");
        return;
      }
      if (isDisabled(button)) return;
      var kind = sourceId === "harmonics-blend" ? "partials" : "tone";
      var frequency = null;
      var promise;
      // The play call happens synchronously inside the user's gesture.
      if (kind === "partials") {
        var partials = blendPartials();
        if (!partials) return;
        promise = client.playPartials({ sourceId: sourceId, partials: partials });
      } else {
        frequency = sourceFrequency(sourceId);
        if (!checkPlayable(frequency).ok) return;
        promise = client.playTone({ sourceId: sourceId, frequency: frequency });
      }
      showSoundMessage("");
      var current = client.getPlayback();
      if (current.ok && current.playback && current.playback.sourceId === sourceId) {
        sound = {
          playbackId: current.playback.playbackId,
          sourceId: sourceId,
          kind: kind,
          status: current.playback.status,
          frequency: frequency
        };
      }
      renderSoundControls();
      promise.then(function (result) {
        if (controller.signal.aborted) return;
        if (!result.ok) {
          var message = result.error ? playMessage(result.error, kind === "partials") : "";
          if (message) showSoundMessage(message);
        }
        renderSoundControls();
      });
    }

    function stopSound(message) {
      if (!sound || !client) return;
      var playbackId = sound.playbackId;
      sound = null;
      client.stop({ playbackId: playbackId });
      if (message) showSoundMessage(message);
      renderSoundControls();
    }

    function retuneTo(frequency) {
      var result = client.retune({ playbackId: sound.playbackId, frequency: frequency });
      if (result.ok) {
        sound.frequency = frequency;
      } else if (result.stopped) {
        sound = null;
        showSoundMessage(playMessage(result.error, false));
      }
      // not_started: the frequency is re-checked when "started" arrives.
    }

    // Applies the §7 trigger matrix to this instance's sound after a change.
    // change.type: tab | reset | mode | input | harmonics | reference | notation.
    function syncSound(change) {
      if (!sound) return;
      var sourceId = sound.sourceId;
      var panel = panelOfSource(sourceId);
      if (change.type === "tab" || (change.type === "reset" && change.panel === panel) ||
          (change.type === "mode" && panel === "convert")) {
        stopSound("");
        return;
      }
      if (change.type === "notation") return;
      if (sourceId === "harmonics-blend") {
        if (change.type === "harmonics") stopSound("");
        return;
      }
      if (change.type === "reference" && !NOTE_SOURCES[sourceId]) return;
      if ((change.type === "input" || change.type === "harmonics") && change.panel !== panel) return;
      if (change.type === "reset") return;
      var frequency = sourceFrequency(sourceId);
      if (frequency == null) {
        stopSound(change.type === "reference" && state.referenceA4 == null ? A4_STOP_MESSAGE : "");
        return;
      }
      var check = checkPlayable(frequency);
      if (!check.ok) {
        stopSound(playMessage(check.error, false));
        return;
      }
      if (sound.status === "playing" && frequency !== sound.frequency) retuneTo(frequency);
    }

    function handleAudioEvent(event) {
      if (controller.signal.aborted) return;
      if (event.type === "device") {
        renderSoundControls();
        return;
      }
      if (!sound || event.playbackId !== sound.playbackId) return;
      if (event.type === "started") {
        sound.status = "playing";
        if (sound.kind === "tone") {
          sound.frequency = event.frequency;
          // Inputs may have changed while the sound was starting.
          var wanted = sourceFrequency(sound.sourceId);
          if (wanted == null || !checkPlayable(wanted).ok) stopSound("");
          else if (wanted !== event.frequency) retuneTo(wanted);
        }
        if (sound) announce(sound.kind === "partials" ? "Playing the harmonic blend." : "Playing " + hzShort(sound.frequency) + ".");
      } else if (event.type === "retuned") {
        sound.frequency = event.frequency;
      } else if (event.type === "stopped") {
        sound = null;
        var message = STOP_MESSAGES[event.reason] || "";
        if (event.reason === "replaced" && event.replacedBy && client && event.replacedBy.clientId === client.id) message = "";
        if (message) showSoundMessage(message);
      }
      renderSoundControls();
    }

    // ---- Tabs -----------------------------------------------------------------

    var currentTab = null;

    function selectTab(name, focus) {
      if (currentTab !== null && currentTab !== name) syncSound({ type: "tab" });
      currentTab = name;
      tabs.forEach(function (tab) {
        var selected = tab.getAttribute("data-ac-freq-pitch-tab") === name;
        tab.setAttribute("aria-selected", selected ? "true" : "false");
        tab.tabIndex = selected ? 0 : -1;
        panels[tab.getAttribute("data-ac-freq-pitch-tab")].hidden = !selected;
        if (selected && focus) tab.focus();
      });
    }

    on(tablist, "click", function (event) {
      var tab = event.target.closest("[data-ac-freq-pitch-tab]");
      if (tab && tablist.contains(tab)) selectTab(tab.getAttribute("data-ac-freq-pitch-tab"), false);
    });

    on(tablist, "keydown", function (event) {
      var current = tabs.indexOf(document.activeElement);
      if (current === -1) return;
      var next = null;
      if (event.key === "ArrowRight" || event.key === "Right") next = (current + 1) % tabs.length;
      else if (event.key === "ArrowLeft" || event.key === "Left") next = (current - 1 + tabs.length) % tabs.length;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = tabs.length - 1;
      if (next === null) return;
      event.preventDefault();
      selectTab(tabs[next].getAttribute("data-ac-freq-pitch-tab"), true);
    });

    // ---- Inputs ---------------------------------------------------------------

    function scopeNameOf(el) {
      if (settings.contains(el)) return "settings";
      var panel = el.closest("[data-ac-freq-pitch-panel]");
      return panel && root.contains(panel) ? panel.getAttribute("data-ac-freq-pitch-panel") : null;
    }

    function handleSettingsInput(target, eventType) {
      var name = target.getAttribute("data-field");
      if (name === "volume") {
        applyVolume();
        return;
      }
      if (name === "referencePreset" && target.value === "custom") {
        updateField("settings", field(settings, "referenceCustom"), "change");
      }
      var notation = readNotation();
      var notationChanged = notation !== state.notation;
      state.notation = notation;
      state.referenceA4 = readReference();
      if (notationChanged) relabelNoteOptions();
      renderAll();
      syncSound({ type: name === "notation" ? "notation" : "reference" });
      renderSoundControls();
      if (eventType === "change" && name === "notation") announce("Note names now use " + (notation === "flat" ? "flats." : "sharps."));
    }

    function handleInput(event) {
      var target = event.target;
      if (!target.matches || !target.matches("[data-field]")) return;
      var scope = scopeNameOf(target);
      if (!scope) return;
      if (target.hasAttribute("data-kind")) updateField(scope, target, event.type);
      if (scope === "settings") {
        handleSettingsInput(target, event.type);
        return;
      }
      var previousHarmonics = state.harmonicsKey;
      RENDERERS[scope]();
      if (scope === "harmonics") {
        // A repeated event with the same values is not a change (D2).
        if (state.harmonicsKey !== previousHarmonics) syncSound({ type: "harmonics", panel: "harmonics" });
      } else {
        syncSound({ type: target.getAttribute("data-field") === "mode" ? "mode" : "input", panel: scope });
      }
      renderSoundControls();
    }

    on(root, "input", handleInput);
    on(root, "change", handleInput);

    on(panels.shift, "click", function (event) {
      var chip = event.target.closest("[data-quick-semitones]");
      if (!chip) return;
      var input = field(panels.shift, "semitones");
      input.value = chip.getAttribute("data-quick-semitones");
      updateField("shift", input, "change");
      renderShift();
      syncSound({ type: "input", panel: "shift" });
      renderSoundControls();
    });

    on(root, "click", function (event) {
      var play = event.target.closest("[data-play]");
      if (play && root.contains(play)) {
        togglePlay(play);
        return;
      }
      var stop = event.target.closest("[data-stop-sound]");
      if (stop && root.contains(stop) && sound) {
        stopSound("");
        announce("Sound stopped.");
      }
    });

    on(root, "keydown", function (event) {
      if ((event.key === "Escape" || event.key === "Esc") && sound) {
        event.preventDefault();
        stopSound("");
        announce("Sound stopped.");
      }
    });

    // ---- Reset ----------------------------------------------------------------

    function resetPanel(panelName) {
      var panel = panels[panelName];
      syncSound({ type: "reset", panel: panelName });
      toArray(panel.querySelectorAll("input[data-kind]")).forEach(function (input) {
        input.value = input.defaultValue;
      });
      toArray(panel.querySelectorAll("select[data-field]")).forEach(resetSelect);
      toArray(panel.querySelectorAll('input[type="radio"][data-field]')).forEach(function (radio) {
        radio.checked = radio.defaultChecked;
      });
      state.copySequence += 1;
      clearCopyState();
      readAllFields(panelName, "initial");
      RENDERERS[panelName]();
      renderSoundControls();
      announce(PANEL_NAMES[panelName] + " reset to defaults.");
    }

    on(root, "click", function (event) {
      var reset = event.target.closest("[data-reset]");
      if (!reset || !root.contains(reset)) return;
      var panel = reset.closest("[data-ac-freq-pitch-panel]");
      if (panel) resetPanel(panel.getAttribute("data-ac-freq-pitch-panel"));
    });

    // ---- Copy -----------------------------------------------------------------

    function fallbackCopy(value) {
      var textarea = document.createElement("textarea");
      var previouslyFocused = document.activeElement;
      textarea.value = value;
      textarea.className = "ac-freq-pitch-copy-fallback";
      textarea.readOnly = true;
      var copied = false;
      try {
        root.appendChild(textarea);
        textarea.focus();
        textarea.setSelectionRange(0, textarea.value.length);
        copied = document.execCommand("copy");
      } catch (error) {
        copied = false;
      } finally {
        if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
        if (previouslyFocused && typeof previouslyFocused.focus === "function") {
          try { previouslyFocused.focus(); } catch (error) { /* best effort */ }
        }
      }
      return copied;
    }

    function copyText(value) {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        return navigator.clipboard.writeText(value).then(
          function () { return true; },
          function () { return fallbackCopy(value); }
        );
      }
      return Promise.resolve(fallbackCopy(value));
    }

    // Copy feedback is shown on one button at a time across the instance.
    function setCopyState(button, copyState) {
      var label = button.querySelector("[data-copy-text]");
      if (copyState) {
        button.setAttribute("data-copy-state", copyState);
        if (label) label.textContent = copyState === "copied" ? "Copied" : "Copy failed";
      } else {
        button.removeAttribute("data-copy-state");
        if (label) label.textContent = label.getAttribute("data-idle-text");
      }
    }

    function clearCopyState() {
      clearTimer("copy");
      if (state.copyButton) setCopyState(state.copyButton, null);
      state.copyButton = null;
    }

    function showCopyFeedback(message, failed) {
      clearTimer("feedback");
      copyFeedback.textContent = message;
      copyFeedback.setAttribute("data-copy-state", failed ? "error" : "copied");
      copyFeedback.hidden = false;
      timers.feedback = window.setTimeout(function () {
        timers.feedback = null;
        copyFeedback.hidden = true;
        copyFeedback.textContent = "";
      }, failed ? COPY_FEEDBACK_MS * 3 : COPY_FEEDBACK_MS * 2);
    }

    function copyFrom(button, value, successMessage, failureMessage) {
      state.copySequence += 1;
      var thisCopy = state.copySequence;
      clearCopyState();

      copyText(value).then(function (copied) {
        if (thisCopy !== state.copySequence || controller.signal.aborted) return;
        state.copyButton = button;
        setCopyState(button, copied ? "copied" : "error");
        announce(copied ? successMessage : failureMessage);
        if (button === copyTable || button.getAttribute("data-copy") === "cell") {
          showCopyFeedback(copied ? successMessage : failureMessage, !copied);
        }
        timers.copy = window.setTimeout(function () {
          timers.copy = null;
          if (thisCopy === state.copySequence) clearCopyState();
        }, COPY_FEEDBACK_MS);
      });
    }

    on(root, "click", function (event) {
      var button = event.target.closest("[data-copy]");
      if (!button || !root.contains(button) || isDisabled(button)) return;
      var value = button.getAttribute("data-value");
      if (!value) return;
      var label = button.getAttribute("data-copy-label") || "value";
      copyFrom(button, value, "Copied " + value + " (" + label + ").",
        "Couldn't copy " + value + ". Your browser blocked clipboard access; select the value and copy it manually.");
    });

    on(copyTable, "click", function () {
      if (isDisabled(copyTable) || !state.tableExport) return;
      copyFrom(copyTable, tableExportText(), "Copied the reference table.",
        "Couldn't copy the table. Your browser blocked clipboard access.");
    });

    // ---- Start ----------------------------------------------------------------

    populateSelects();
    buildTable();
    readAllFields("settings", "initial");
    ["convert", "shift", "harmonics"].forEach(function (name) { readAllFields(name, "initial"); });
    state.notation = readNotation();
    state.referenceA4 = readReference();
    relabelNoteOptions();
    state.volume = readVolume();
    volumeOutput.textContent = state.volume + "%";

    if (audioApi && typeof audioApi.isSupported === "function" && audioApi.isSupported()) {
      var created = audioApi.createClient({ volume: state.volume, onChange: handleAudioEvent });
      if (created && created.ok) client = created.client;
    }
    audioNotice.hidden = !!client;

    renderConvert();
    renderShift();
    renderHarmonics();
    renderSoundControls();
    selectTab("convert", false);
    root.setAttribute("data-state", "ready");

    return {
      destroy: function () {
        controller.abort();
        Object.keys(timers).forEach(clearTimer);
        if (client) client.destroy();
        client = null;
        sound = null;
      }
    };
  }

  function rootsIn(scope) {
    if (!scope || typeof scope.querySelectorAll !== "function") return [];
    var roots = Array.prototype.slice.call(scope.querySelectorAll("[data-ac-freq-pitch]"));
    if (scope.matches && scope.matches("[data-ac-freq-pitch]")) roots.unshift(scope);
    return roots;
  }

  function init(scope) {
    var core = window.AudiocolourFrequencyPitchCore;
    var audio = window.AudiocolourFrequencyPitchAudio || null;
    rootsIn(scope || document).forEach(function (root) {
      if (instances.has(root)) return;
      var instance = core ? createInstance(root, core, audio) : null;
      if (!instance) {
        var notice = root.querySelector("[data-ac-freq-pitch-unavailable]");
        if (notice) notice.hidden = false;
        root.setAttribute("data-state", "unavailable");
        return;
      }
      instances.set(root, instance);
    });
  }

  function destroy(scope) {
    rootsIn(scope || document).forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  window.AudiocolourFrequencyPitch = Object.freeze({ init: init, destroy: destroy });

  // Theme editor: sections are re-rendered in place.
  document.addEventListener("shopify:section:unload", function (event) { destroy(event.target); });
  document.addEventListener("shopify:section:load", function (event) { init(event.target); });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { init(document); });
  } else {
    init(document);
  }
}());
